import UseContext from 'hooks/useContext';
import MissionPerimeterField from 'components/perimeter/MissionPerimeterField';
import {
  formatTemplateAddress,
  formatTemplateContact,
} from './missionTemplateFormat';
import { MISSION_INSTRUCTIONS_MAX_LENGTH } from './templateMissionData';
import { templateText } from './templateTexts';

const ReadOnlyField = ({ label, value }) => (
  <div className="flex flex-col gap-1">
    {label ? <span className="ft-sm text-gray-500">{label}</span> : null}
    <div className="w-full min-h-10 px-3 py-2 ft-sm text-gray-900 bg-gray-50 border border-gray-100 rounded-lg break-words">
      {value || '-'}
    </div>
  </div>
);

const InstructionField = ({ id, label, placeholder, value, onChange }) => (
  <div className="flex flex-col gap-1">
    <label htmlFor={id} className="ft-sm text-gray-500">
      {label}
    </label>
    <textarea
      id={id}
      value={value || ''}
      maxLength={MISSION_INSTRUCTIONS_MAX_LENGTH}
      rows={3}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      className="w-full px-3 py-2 text-sm placeholder-gray-600 border border-gray-200 rounded-lg focus:outline-none"
    />
  </div>
);

/** What the mission adds to the job description: its instructions and its
 *  reference. The address and the manager belong to the template, so they are
 *  read here and changed there. */
const TemplateMissionDetailsStep = ({ perimeterField }) => {
  const { dataCreateMission, setDataCreateMission } = UseContext();
  const template = dataCreateMission.missionTemplate;
  const setField = (key) => (value) =>
    setDataCreateMission((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <span className="ft-xl font-medium text-gray-900">
            Adresse de la mission
          </span>
          <span className="ft-sm text-gray-500">
            Elle vient de la fiche de poste. Pour la changer, modifiez la fiche.
          </span>
        </div>
        <ReadOnlyField value={formatTemplateAddress(template?.location)} />
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <span className="ft-xl font-medium text-gray-900">Manager</span>
          <span className="ft-sm text-gray-500">
            La personne en charge de l&apos;intérimaire, telle que la fiche la
            porte.
          </span>
        </div>
        <ReadOnlyField value={formatTemplateContact(template)} />
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <span className="ft-xl font-medium text-gray-900">
            {templateText('mission.instructionsTitle')}
          </span>
          <span className="ft-sm text-gray-500">
            {templateText('mission.instructionsDescription')}
          </span>
        </div>
        <InstructionField
          id="template-mission-other"
          label={templateText('mission.instructionsInterim')}
          placeholder={templateText('mission.instructionsInterimPlaceholder')}
          value={dataCreateMission.other}
          onChange={setField('other')}
        />
        <InstructionField
          id="template-mission-other-interim"
          label={templateText('mission.instructionsJobbiz')}
          placeholder={templateText('mission.instructionsJobbizPlaceholder')}
          value={dataCreateMission.other_interim}
          onChange={setField('other_interim')}
        />
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <span className="ft-xl font-medium text-gray-900">Référence</span>
          <span className="ft-sm text-gray-500">
            Votre référence interne pour cette mission.
          </span>
        </div>
        <input
          type="text"
          value={dataCreateMission.reference || ''}
          placeholder="Référence interne (optionnel)"
          aria-label="Référence interne"
          className="w-full h-10 px-3 ft-b placeholder-gray-600 border rounded-lg text-sm"
          onChange={(event) => setField('reference')(event.target.value)}
        />
      </div>

      {perimeterField ? <MissionPerimeterField field={perimeterField} /> : null}
    </div>
  );
};

export default TemplateMissionDetailsStep;
